import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, withModifiers as _withModifiers, withKeys as _withKeys, TransitionGroup as _TransitionGroup } from "vue"

const _hoisted_1 = {
  class: "table-views",
  "data-testid": "table-views-controls"
}
const _hoisted_2 = {
  class: "sr-only",
  role: "status",
  "aria-live": "polite",
  "data-testid": "table-views-announcement"
}
const _hoisted_3 = { class: "view-controls" }
const _hoisted_4 = { class: "query-grow query-column" }
const _hoisted_5 = ["data-testid"]
const _hoisted_6 = { class: "view-menu" }
const _hoisted_7 = { class: "menu-nav-value" }
const _hoisted_8 = ["aria-label"]
const _hoisted_9 = { class: "menu-nav-value" }
const _hoisted_10 = {
  key: 0,
  class: "icon icon-lock column-handle"
}
const _hoisted_11 = ["data-testid", "onMousedown"]

import { computed, nextTick, ref, watch } from 'vue';
import { useStore } from 'vuex';

import TableViewQueryInput from '@shell/components/TableViews/TableViewQueryInput.vue';
import { useDragReorder } from '@shell/composables/useDragReorder';
import { useI18n } from '@shell/composables/useI18n';
import { validateQuery } from '@shell/utils/table-views/query';
import { NO_GROUPING, isViewModified } from '@shell/utils/table-views/views';
import { isMac } from '@shell/utils/platform';
import type { TableViewField, TableViewQueryProblem, TableViewRow, TableViewState } from '@shell/types/table-views';
import {
  RcDropdown,
  RcDropdownItem,
  RcDropdownItemCheckbox,
  RcDropdownItemRadio,
  RcDropdownSeparator,
  RcDropdownSubmenu,
  RcDropdownTrigger
} from '@components/RcDropdown';

/** Clears the row's hover highlight rather than sitting over the handle */
const TOOLTIP_DISTANCE = 12;


export default /*@__PURE__*/_defineComponent({
  __name: 'TableViewControls',
  props: {
    view: {},
    coreColumns: { default: () => [] },
    defaultColumns: { default: () => [] },
    unsupportedFields: { default: () => [] },
    fields: { default: () => [] },
    groupFields: { default: null },
    defaultGroupBy: { default: null },
    filterFields: { default: null },
    dateFields: { default: () => [] },
    fieldValues: { default: () => ({}) },
    pendingFields: { default: () => [] },
    loadingValues: { default: () => [] },
    rows: { default: () => [] },
    historyKey: { default: 0 }
  },
  emits: ["update:view", "request-values", "refresh-values"],
  setup(__props: any, { emit: __emit }) {

/**
 * The row under the view tabs: the filter query and the View menu (grouping and columns). It edits
 * the `view` prop and hands it back; the tabs above see the change through the table
 */
const props = __props;

const emit = __emit;

const store = useStore();

const { t } = useI18n(store);

const columnsPanel = ref<HTMLElement | null>(null);

const groupPanel = ref<HTMLElement | null>(null);

const queryFocused = ref(false);

const columnSlots = ref<{ top: number, bottom: number }[] | null>(null);

/**
 * Said by a screen reader: what a command in the menu changed, which the focus, left on the command,
 * doesn't show
 */
const announcement = ref('');

/** Cleared first, so saying the same thing twice is heard twice */
const announce = async(message: string) => {
  announcement.value = '';
  await nextTick();
  announcement.value = message;
};

const moveKeysHint = computed(() => t('tableViews.columns.moveKeys', { modifier: isMac ? 'Option' : 'Alt' }));

/** Held back while the box has the caret, so `state:` isn't reported halfway through `state:active` */
const shownProblems = computed(() => (queryFocused.value ? [] : validateQuery(props.view.query, props.fields)));

const unsupportedNotice = computed(() => t('tableViews.query.unsupported', {
  count:  props.unsupportedFields.length,
  fields: props.unsupportedFields.join(', '),
}, true));

const problemNotice = (problem: TableViewQueryProblem) => t(`tableViews.query.problem.${ problem.kind }`, { text: problem.text, label: problem.label || '' }, true);

/** A query that can't be read is reported alone, before any unfilterable fields */
const queryStatusMessage = computed(() => {
  if (shownProblems.value.length) {
    return shownProblems.value.map((problem) => problemNotice(problem)).join('<br>');
  }

  return props.unsupportedFields.length ? unsupportedNotice.value : '';
});

const queryStatus = computed(() => (shownProblems.value.length ? 'error' : 'info'));

const columnFields = computed(() => props.fields.filter((f) => !f.isLabel && !f.queryOnly));

/** Empty content hides the tooltip once a row is picked up */
const reorderTip = computed(() => ({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  content: heldColumnId.value ? '' : t('tableViews.columns.reorder'), placement: 'left', distance: TOOLTIP_DISTANCE
}));

const lockedTip = computed(() => ({
  content: t('tableViews.columns.locked'), placement: 'left', distance: TOOLTIP_DISTANCE
}));

const orderedColumnFields = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const order = columnDragOrder.value || props.view.columnOrder;

  if (!order?.length) {
    return columnFields.value;
  }

  const byId: Record<string, TableViewField> = {};

  columnFields.value.forEach((f) => {
    byId[f.id] = f;
  });

  const out = order.map((id: string) => byId[id]).filter((f) => !!f);

  return out.concat(columnFields.value.filter((f) => !order.includes(f.id)));
});

const isColumnVisible = (field: TableViewField) => {
  if (props.view.columns) {
    return props.view.columns.includes(field.id);
  }

  // Nothing chosen yet, so the page's own set is what is shown
  return !props.defaultColumns.length || props.defaultColumns.includes(field.id);
};

/** The table's own columns, grouping, or all of it: then the matching Reset has nothing to undo */
const columnsAsTheyCome = computed(() => !props.view.columns && !props.view.labelColumns?.length && !props.view.columnOrder);

const groupingAsItComes = computed(() => !props.view.groupBy);

const viewAsItComes = computed(() => !isViewModified(props.view));

const visibleColumnCount = computed(() => columnFields.value.filter((f) => isColumnVisible(f)).length + (props.view.labelColumns?.length || 0));

const columnsSummary = computed(() => {
  if (columnsAsTheyCome.value) {
    return t('tableViews.view.columnsDefault');
  }

  // Label columns only count once added, or a pod list reads "9 / 42"
  return t('tableViews.view.columnsCount', { shown: visibleColumnCount.value, total: columnFields.value.length + (props.view.labelColumns?.length || 0) });
});

/** Labels are left out: there can be any number of them */
const groupOptions = computed(() => {
  const none = { id: null as string | null, label: t('tableViews.group.none') };

  return [none].concat((props.groupFields || props.fields)
    .filter((f) => !f.isLabel)
    .map((f) => ({ id: f.id as string | null, label: f.label })));
});

/** The grouping in force: the view's, else the table's default */
const appliedGroupBy = computed(() => (props.view.groupBy === NO_GROUPING ? null : props.view.groupBy || props.defaultGroupBy || null));

const groupLabel = computed(() => groupOptions.value.find((o) => o.id === appliedGroupBy.value)?.label || t('tableViews.group.none'));

const update = (changes: Partial<TableViewState>) => emit('update:view', { ...props.view, ...changes });

const isCoreColumn = (field: TableViewField) => !!field?.id && props.coreColumns.includes(field.id);

const toggleColumn = (field: TableViewField) => {
  if (isCoreColumn(field)) {
    return;
  }

  // From what is on screen, or toggling one would turn on the columns the page leaves out
  const current = props.view.columns || columnFields.value.filter((f) => isColumnVisible(f)).map((f) => f.id);
  const next = current.includes(field.id) ? current.filter((id: string) => id !== field.id) : current.concat([field.id]);

  update({ columns: next });
};

const selectAllColumns = () => {
  update({ columns: columnFields.value.map((f) => f.id) });
  announce(t('tableViews.columns.allShown', { count: columnFields.value.length }));
};

/**
 * Measured once as the drag begins, in the panel's coordinates: displaced rows are mid-transition
 * afterwards, and the panel may scroll
 */
const captureColumnSlots = () => {
  const scroller = columnsPanel.value;
  const rows = scroller?.querySelectorAll('[data-col-id]') || [];
  const origin = scroller ? scroller.getBoundingClientRect().top - scroller.scrollTop : 0;

  columnSlots.value = Array.from(rows).map((el) => {
    const box = el.getBoundingClientRect();

    return { top: box.top - origin, bottom: box.bottom - origin };
  });
};

/** Clamped, so dragging past the last row parks the column at the end */
const columnIndexAt = (clientY: number) => {
  const slots = columnSlots.value || [];
  const scroller = columnsPanel.value;

  if (!slots.length || !scroller) {
    return -1;
  }

  const y = clientY - scroller.getBoundingClientRect().top + scroller.scrollTop;

  if (y <= slots[0].top) {
    return 0;
  }

  if (y >= slots[slots.length - 1].bottom) {
    return slots.length - 1;
  }

  return slots.findIndex((slot) => y >= slot.top && y <= slot.bottom);
};

/** Locked columns hold the head of the list, so nothing may be carried above them */
const firstMovableIndex = (order: string[]) => {
  let i = 0;

  while (i < order.length && props.coreColumns.includes(order[i])) {
    i++;
  }

  return i;
};

const { heldId: heldColumnId, order: columnDragOrder, start: armColumnDrag } = useDragReorder({
  axis:         'y',
  initialOrder: () => orderedColumnFields.value.map((f) => f.id),
  measure:      captureColumnSlots,
  indexAt:      columnIndexAt,
  firstMovable: firstMovableIndex,
  onEnd:        () => {
    columnSlots.value = null;
  },
  onCommit: (order) => update({ columnOrder: order }),
});

const startColumnDrag = (id: string, event: MouseEvent) => {
  if (event.button !== 0) {
    return;
  }

  // Otherwise the pointer selects the labels it crosses
  event.preventDefault();
  armColumnDrag(id, event);
};

/** The table's default is stored as nothing, so it isn't a change; none over a default is stored as such */
const pickGroupBy = (id: string | null) => update({ groupBy: id === props.defaultGroupBy ? null : id || NO_GROUPING });

/** Picking the applied grouping again removes it */
const toggleGroupBy = (id: string | null) => pickGroupBy(id === appliedGroupBy.value ? null : id);

const resetColumns = () => {
  update({
    columns: null, labelColumns: [], columnOrder: null
  });
  announce(t('tableViews.columns.resetDone'));
};

const resetGroupBy = async() => {
  update({ groupBy: null });
  // The table's default grouping, once the view has taken the change
  await nextTick();
  announce(t('tableViews.group.resetDone', { label: groupLabel.value }));
};

/** The keyboard's way to reorder: one place up or down, never above the locked columns */
const moveColumn = async(field: TableViewField, step: number) => {
  const order = orderedColumnFields.value.map((f) => f.id);
  const from = order.indexOf(field.id);
  const to = from + step;

  if (isCoreColumn(field) || to < firstMovableIndex(order) || to >= order.length) {
    return;
  }

  order.splice(to, 0, order.splice(from, 1)[0]);
  update({ columnOrder: order });
  announce(t('tableViews.columns.moved', {
    label: field.label, position: to + 1, total: order.length
  }));

  // Moving the row takes it out of the page for a moment, which drops its focus
  await nextTick();
  (columnsPanel.value?.querySelector(`[data-col-id="${ field.id }"]`) as HTMLElement | null)?.focus();
};

const resetView = () => update({
  query: '', columns: null, labelColumns: [], columnOrder: null, groupBy: null, sort: null, sortDescending: false
});

/**
 * Scroll the grouped field into view a frame after the list opens, by the list alone: `scrollIntoView`
 * would scroll the page too, moving the menu from under the pointer while it is being placed
 */
watch(groupPanel, (panel) => {
  if (!panel) {
    return;
  }

  requestAnimationFrame(() => {
    const option = panel.querySelector(`[data-testid="table-views-group-${ appliedGroupBy.value || 'none' }"]`);

    if (!option) {
      return;
    }

    const list = panel.getBoundingClientRect();
    const at = option.getBoundingClientRect();

    if (at.top < list.top) {
      panel.scrollTop -= list.top - at.top;
    } else if (at.bottom > list.bottom) {
      panel.scrollTop += at.bottom - list.bottom;
    }
  });
});

return (_ctx: any,_cache: any) => {
  const _directive_clean_html = _resolveDirective("clean-html")!
  const _directive_clean_tooltip = _resolveDirective("clean-tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, _toDisplayString(announcement.value), 1),
    _cache[21] || (_cache[21] = _createTextVNode()),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createVNode(TableViewQueryInput, {
          value: __props.view.query,
          fields: __props.fields,
          "filter-fields": __props.filterFields,
          "date-fields": __props.dateFields,
          rows: __props.rows,
          "field-values": __props.fieldValues,
          "pending-fields": __props.pendingFields,
          "loading-values": __props.loadingValues,
          "history-key": __props.historyKey,
          "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => (update({ query: $event }))),
          "onUpdate:focused": _cache[1] || (_cache[1] = ($event: any) => (queryFocused.value = $event)),
          onRequestValues: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('request-values', $event))),
          onRefreshValues: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('refresh-values', $event)))
        }, null, 8, ["value", "fields", "filter-fields", "date-fields", "rows", "field-values", "pending-fields", "loading-values", "history-key"]),
        _cache[5] || (_cache[5] = _createTextVNode()),
        (queryStatusMessage.value)
          ? (_openBlock(), _createElementBlock("p", {
              key: 0,
              class: _normalizeClass(['query-notice', queryStatus.value]),
              role: "alert",
              "data-testid": queryStatus.value === 'error' ? 'table-views-query-problem' : 'table-views-unsupported'
            }, [
              _createElementVNode("i", {
                class: _normalizeClass(["icon", queryStatus.value === 'error' ? 'icon-notify-warning' : 'icon-notify-info'])
              }, null, 2),
              _cache[4] || (_cache[4] = _createTextVNode()),
              _withDirectives(_createElementVNode("span", null, null, 512), [
                [_directive_clean_html, queryStatusMessage.value]
              ])
            ], 10, _hoisted_5))
          : _createCommentVNode("", true)
      ]),
      _cache[20] || (_cache[20] = _createTextVNode()),
      _createVNode(_unref(RcDropdown), {
        placement: 'bottom-end',
        "aria-label": _unref(t)('tableViews.view.label')
      }, {
        dropdownCollection: _withCtx(() => [
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_unref(RcDropdownSubmenu), {
              side: "left",
              "data-testid": "table-views-view-group"
            }, {
              after: _withCtx(() => [
                _createElementVNode("span", _hoisted_7, _toDisplayString(groupLabel.value), 1)
              ]),
              submenu: _withCtx(() => [
                _createElementVNode("div", {
                  ref_key: "groupPanel",
                  ref: groupPanel,
                  class: "menu-panel"
                }, [
                  _createElementVNode("div", {
                    role: "group",
                    "aria-label": _unref(t)('tableViews.view.groupBy')
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupOptions.value, (option) => {
                      return (_openBlock(), _createBlock(_unref(RcDropdownItemRadio), {
                        key: option.id || 'none',
                        checked: option.id === appliedGroupBy.value,
                        "data-testid": `table-views-group-${ option.id || 'none' }`,
                        onClick: ($event: any) => (toggleGroupBy(option.id))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(option.label), 1)
                        ]),
                        _: 2
                      }, 1032, ["checked", "data-testid", "onClick"]))
                    }), 128))
                  ], 8, _hoisted_8),
                  _cache[7] || (_cache[7] = _createTextVNode()),
                  _createVNode(_unref(RcDropdownSeparator)),
                  _cache[8] || (_cache[8] = _createTextVNode()),
                  _createVNode(_unref(RcDropdownItem), {
                    class: "menu-reset",
                    "acts-on-checkable-items": "",
                    "data-testid": "table-views-group-reset",
                    disabled: groupingAsItComes.value,
                    onClick: resetGroupBy
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)('tableViews.view.reset')), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled"])
                ], 512)
              ]),
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('tableViews.view.groupBy')) + " ", 1),
                _cache[9] || (_cache[9] = _createTextVNode())
              ]),
              _: 1
            }),
            _cache[16] || (_cache[16] = _createTextVNode()),
            _createVNode(_unref(RcDropdownSubmenu), {
              side: "left",
              "data-testid": "table-views-view-columns"
            }, {
              after: _withCtx(() => [
                _createElementVNode("span", _hoisted_9, _toDisplayString(columnsSummary.value), 1)
              ]),
              submenu: _withCtx(() => [
                _createElementVNode("div", {
                  ref_key: "columnsPanel",
                  ref: columnsPanel,
                  class: "menu-panel columns-panel"
                }, [
                  _createVNode(_TransitionGroup, {
                    name: "column-row",
                    tag: "div",
                    role: "group",
                    "aria-label": _unref(t)('tableViews.view.columnsConfiguration'),
                    class: _normalizeClass({ 'is-reordering': _unref(heldColumnId) !== null })
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(orderedColumnFields.value, (field) => {
                        return (_openBlock(), _createBlock(_unref(RcDropdownItemCheckbox), {
                          key: field.id,
                          indicator: "checkmark",
                          "model-value": isColumnVisible(field),
                          class: _normalizeClass({ 'column-row': true, locked: isCoreColumn(field), shown: isColumnVisible(field), held: _unref(heldColumnId) === field.id }),
                          disabled: isCoreColumn(field),
                          "data-col-id": field.id,
                          "data-testid": `table-views-col-${ field.id }`,
                          "aria-description": isCoreColumn(field) ? _unref(t)('tableViews.columns.locked') : moveKeysHint.value,
                          onClick: ($event: any) => (toggleColumn(field)),
                          onKeydown: [
                            _withKeys(_withModifiers(($event: any) => (moveColumn(field, -1)), ["alt","prevent","stop"]), ["up"]),
                            _withKeys(_withModifiers(($event: any) => (moveColumn(field, 1)), ["alt","prevent","stop"]), ["down"])
                          ]
                        }, {
                          before: _withCtx(() => [
                            (isCoreColumn(field))
                              ? _withDirectives((_openBlock(), _createElementBlock("i", _hoisted_10, null, 512)), [
                                  [_directive_clean_tooltip, lockedTip.value]
                                ])
                              : _withDirectives((_openBlock(), _createElementBlock("span", {
                                  key: 1,
                                  class: "column-handle grip",
                                  "data-testid": `table-views-col-handle-${ field.id }`,
                                  onMousedown: ($event: any) => (startColumnDrag(field.id, $event))
                                }, null, 40, _hoisted_11)), [
                                  [_directive_clean_tooltip, reorderTip.value]
                                ])
                          ]),
                          default: _withCtx(() => [
                            _createTextVNode(" " + _toDisplayString(field.label), 1)
                          ]),
                          _: 2
                        }, 1032, ["model-value", "class", "disabled", "data-col-id", "data-testid", "aria-description", "onClick", "onKeydown"]))
                      }), 128))
                    ]),
                    _: 1
                  }, 8, ["aria-label", "class"]),
                  _cache[12] || (_cache[12] = _createTextVNode()),
                  _createVNode(_unref(RcDropdownSeparator)),
                  _cache[13] || (_cache[13] = _createTextVNode()),
                  _createVNode(_unref(RcDropdownItem), {
                    "acts-on-checkable-items": "",
                    "data-testid": "table-views-columns-select-all",
                    onClick: selectAllColumns
                  }, {
                    before: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                      _createElementVNode("i", { class: "menu-gutter" }, null, -1)
                    ]))]),
                    default: _withCtx(() => [
                      _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.columns.selectAll')), 1)
                    ]),
                    _: 1
                  }),
                  _cache[14] || (_cache[14] = _createTextVNode()),
                  _createVNode(_unref(RcDropdownItem), {
                    class: "menu-reset",
                    "acts-on-checkable-items": "",
                    "data-testid": "table-views-columns-reset",
                    disabled: columnsAsTheyCome.value,
                    onClick: resetColumns
                  }, {
                    before: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                      _createElementVNode("i", { class: "menu-gutter" }, null, -1)
                    ]))]),
                    default: _withCtx(() => [
                      _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.columns.reset')), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled"])
                ], 512)
              ]),
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('tableViews.view.columnsConfiguration')) + " ", 1),
                _cache[15] || (_cache[15] = _createTextVNode())
              ]),
              _: 1
            }),
            _cache[17] || (_cache[17] = _createTextVNode()),
            _createVNode(_unref(RcDropdownSeparator)),
            _cache[18] || (_cache[18] = _createTextVNode()),
            _createVNode(_unref(RcDropdownItem), {
              class: "menu-reset",
              "data-testid": "table-views-reset",
              disabled: viewAsItComes.value,
              onClick: resetView
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('tableViews.view.reset')), 1)
              ]),
              _: 1
            }, 8, ["disabled"])
          ])
        ]),
        default: _withCtx(() => [
          _createVNode(_unref(RcDropdownTrigger), {
            variant: "tertiary",
            class: "view-control-btn",
            "data-testid": "table-views-view-menu"
          }, {
            before: _withCtx(() => [...(_cache[6] || (_cache[6] = [
              _createElementVNode("i", { class: "icon icon-gear" }, null, -1)
            ]))]),
            default: _withCtx(() => [
              _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.view.label')), 1)
            ]),
            _: 1
          }),
          _cache[19] || (_cache[19] = _createTextVNode())
        ]),
        _: 1
      }, 8, ["aria-label"])
    ])
  ]))
}
}

})